import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { 
  Home, Building2, Hammer, Wrench, ArrowRight, 
  CheckCircle2, Shield, Clock, Users, Paintbrush, 
  Bath, UtensilsCrossed, Grid3X3
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Navigation, Footer } from "@/pages/LandingPage";
import { useLang } from "@/lib/i18n";

const services = [
  {
    id: "bathroom",
    icon: Bath,
    title: { en: "Bathroom Remodeling", es: "Remodelación de Baños" },
    description: {
      en: "Transform your bathroom into a beautiful, functional space. We handle everything from simple updates to complete renovations, including showers, tubs, vanities, and tile work.",
      es: "Convierta su baño en un espacio bonito y funcional. Hacemos desde cambios sencillos hasta renovaciones completas, incluyendo regaderas, tinas, lavabos y azulejo."
    },
    features: [
      { en: "Shower & tub installation", es: "Instalación de regaderas y tinas" },
      { en: "Vanity & cabinet work", es: "Lavabos y gabinetes" },
      { en: "Tile & flooring", es: "Azulejo y pisos" },
      { en: "Plumbing fixtures", es: "Accesorios de plomería" },
      { en: "Lighting upgrades", es: "Mejoras de iluminación" },
      { en: "Accessibility modifications", es: "Adaptaciones de accesibilidad" }
    ],
    image: "https://customer-assets.emergentagent.com/job_site-snap/artifacts/4unhndch_20240806_172201.jpg"
  },
  {
    id: "kitchen",
    icon: UtensilsCrossed,
    title: { en: "Kitchen Remodeling", es: "Remodelación de Cocinas" },
    description: {
      en: "Create your dream kitchen with our expert remodeling services. From custom cabinets to modern countertops, we bring your vision to life with quality craftsmanship.",
      es: "Cree la cocina de sus sueños con nuestros servicios de remodelación. Desde gabinetes a la medida hasta cubiertas modernas, hacemos realidad su idea con trabajo de calidad."
    },
    features: [
      { en: "Custom cabinets", es: "Gabinetes a la medida" },
      { en: "Countertop installation", es: "Instalación de cubiertas" },
      { en: "Backsplash & tile", es: "Backsplash y azulejo" },
      { en: "Sink & faucet install", es: "Instalación de fregadero y llave" },
      { en: "Lighting & electrical", es: "Iluminación y electricidad" },
      { en: "Flooring updates", es: "Renovación de pisos" }
    ],
    image: "https://customer-assets.emergentagent.com/job_ccf15a11-6d33-4c9c-9ff0-3f0c213b7bbc/artifacts/kehpr6ol_20250821_174834.jpg"
  },
  {
    id: "flooring",
    icon: Grid3X3,
    title: { en: "Flooring & Tile", es: "Pisos y Azulejo" },
    description: {
      en: "Professional flooring installation for every room in your home. We work with all types of materials including hardwood, laminate, vinyl, and ceramic tile.",
      es: "Instalación profesional de pisos para cada cuarto de su casa. Trabajamos con todo tipo de materiales: madera, laminado, vinil y azulejo cerámico."
    },
    features: [
      { en: "Hardwood flooring", es: "Pisos de madera" },
      { en: "Laminate installation", es: "Instalación de laminado" },
      { en: "Vinyl & LVP", es: "Vinil y LVP" },
      { en: "Ceramic & porcelain tile", es: "Azulejo cerámico y porcelanato" },
      { en: "Floor repair & refinishing", es: "Reparación y restauración de pisos" },
      { en: "Subfloor preparation", es: "Preparación del subpiso" }
    ],
    image: "/images/floor2_rotated.webp"
  },
  {
    id: "painting",
    icon: Paintbrush,
    title: { en: "Painting Services", es: "Servicios de Pintura" },
    description: {
      en: "Professional interior and exterior painting services. We use high-quality paints and expert techniques to give your home a fresh, beautiful look that lasts.",
      es: "Pintura profesional de interiores y exteriores. Usamos pinturas de alta calidad y técnicas expertas para darle a su casa un aspecto nuevo y duradero."
    },
    features: [
      { en: "Interior painting", es: "Pintura interior" },
      { en: "Exterior painting", es: "Pintura exterior" },
      { en: "Cabinet refinishing", es: "Restauración de gabinetes" },
      { en: "Deck & fence staining", es: "Tinte de decks y cercas" },
      { en: "Drywall repair", es: "Reparación de drywall" },
      { en: "Color consultation", es: "Asesoría de colores" }
    ],
    image: "/images/zqu7urv3_rotated.webp"
  },
  {
    id: "handyman",
    icon: Wrench,
    title: { en: "Handyman Services", es: "Reparaciones Generales" },
    description: {
      en: "From small repairs to larger projects, our handyman services cover all your home maintenance needs. No job is too small for our experienced team.",
      es: "Desde reparaciones pequeñas hasta proyectos más grandes, cubrimos todas las necesidades de mantenimiento de su casa. Ningún trabajo es demasiado pequeño para nuestro equipo."
    },
    features: [
      { en: "Faucet replacement", es: "Cambio de llaves de agua" },
      { en: "Electrical repairs", es: "Reparaciones eléctricas" },
      { en: "Plumbing fixes", es: "Reparaciones de plomería" },
      { en: "Window replacement", es: "Cambio de ventanas" },
      { en: "Door installation", es: "Instalación de puertas" },
      { en: "General repairs", es: "Reparaciones en general" }
    ],
    image: "https://customer-assets.emergentagent.com/job_ccf15a11-6d33-4c9c-9ff0-3f0c213b7bbc/artifacts/au1bmwyd_20240309_122530.jpg"
  }
];

const benefits = [
  {
    icon: Shield,
    title: { en: "Licensed & Insured", es: "Con Licencia y Seguro" },
    description: {
      en: "Fully licensed contractors with comprehensive insurance coverage for your protection.",
      es: "Contratistas con licencia completa y seguro amplio para su protección."
    }
  },
  {
    icon: Clock,
    title: { en: "On-Time Delivery", es: "Entrega a Tiempo" },
    description: {
      en: "We respect your timeline with meticulous project management and clear communication.",
      es: "Respetamos sus tiempos con una buena organización del proyecto y comunicación clara."
    }
  },
  {
    icon: Users,
    title: { en: "Expert Team", es: "Equipo Experto" },
    description: {
      en: "Skilled professionals with years of experience in all aspects of home remodeling.",
      es: "Profesionales con años de experiencia en todo tipo de remodelación de casas."
    }
  },
  {
    icon: CheckCircle2,
    title: { en: "Quality Guarantee", es: "Garantía de Calidad" },
    description: {
      en: "We stand behind our work with comprehensive warranties and satisfaction guarantee.",
      es: "Respaldamos nuestro trabajo con garantías completas y su satisfacción asegurada."
    }
  }
];

export default function ServicesPage() {
  const location = useLocation();
  const { t } = useLang();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      // Small delay to ensure DOM is rendered before scrolling
      const t = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          const yOffset = -100; // Offset to account for sticky nav
          const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 200);
      return () => clearTimeout(t);
    } else {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [location.hash, location.pathname]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-blue-900 relative overflow-hidden" data-testid="services-hero">
        <div className="absolute inset-0 grid-bg opacity-10"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.p 
            className="font-mono text-xs tracking-[0.2em] uppercase text-emerald-400 mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {t("Our Expertise", "Nuestra Experiencia")}
          </motion.p>
          <motion.h1 
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight uppercase leading-none mb-6 font-['Archivo']"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            {t("Our", "Nuestros")}<br />{t("Services", "Servicios")}
          </motion.h1>
          <motion.p 
            className="text-blue-200 text-base md:text-lg leading-relaxed max-w-2xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {t(
              "Full residential remodeling services for interior and exterior. From bathrooms to kitchens, flooring to painting - we do it all.",
              "Servicios completos de remodelación residencial, interior y exterior. Desde baños hasta cocinas, pisos y pintura: lo hacemos todo."
            )}
          </motion.p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24" data-testid="services-grid">
        <div className="max-w-7xl mx-auto px-6">
          <div className="space-y-24">
            {services.map((service, i) => (
              <motion.div 
                key={service.id}
                id={service.id}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center scroll-mt-24 ${i % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                data-testid={`service-detail-${service.id}`}
              >
                <div className={i % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="aspect-[4/3] overflow-hidden bg-slate-200 flex items-center justify-center">
                    {service.image ? (
                      <img 
                        src={service.image} 
                        alt={t(service.title)}
                        data-testid={`service-image-${service.id}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="text-center p-8">
                        <service.icon className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
                        <p className="text-slate-500 text-sm">{t("Photo coming soon", "Foto próximamente")}</p>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className={i % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="w-14 h-14 bg-blue-900 flex items-center justify-center mb-6">
                    <service.icon className="w-7 h-7 text-emerald-400" />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-blue-900 tracking-tight uppercase mb-4 font-['Archivo']">
                    {t(service.title)}
                  </h2>
                  <p className="text-slate-600 leading-relaxed mb-6">
                    {t(service.description)}
                  </p>
                  
                  <div className="grid grid-cols-2 gap-3 mb-8">
                    {service.features.map((feature, j) => (
                      <div key={j} className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                        <span className="text-sm text-slate-700">{t(feature)}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex flex-wrap gap-3">
                    <Link to="/contact">
                      <Button className="bg-blue-900 text-white hover:bg-emerald-600 rounded-sm px-8 py-4 font-bold tracking-wide transition-all duration-300 uppercase text-sm" data-testid={`service-quote-${service.id}`}>
                        {t("Get a Quote", "Cotizar")} <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                    <Link to={`/gallery/${service.id}`}>
                      <Button variant="outline" className="border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white rounded-sm px-8 py-4 font-bold tracking-wide transition-all duration-300 uppercase text-sm" data-testid={`service-gallery-${service.id}`}>
                        {t("View Gallery", "Ver Galería")}
                      </Button>
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-white" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-emerald-600 mb-2">{t("Why Choose Us", "Por Qué Elegirnos")}</p>
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 tracking-tight uppercase font-['Archivo']">
              {t("The Finix Terra Difference", "La Diferencia Finix Terra")}
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, i) => (
              <motion.div 
                key={i}
                className="bg-slate-50 border border-slate-200 p-6 text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <div className="w-12 h-12 bg-blue-900 flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="font-bold text-blue-900 mb-2 font-['Archivo'] uppercase text-sm tracking-wide">{t(benefit.title)}</h3>
                <p className="text-sm text-slate-600">{t(benefit.description)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-blue-900" data-testid="services-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight uppercase mb-6 font-['Archivo']">
            {t("Ready to Get Started?", "¿Listo Para Empezar?")}
          </h2>
          <p className="text-blue-200 text-base md:text-lg mb-8">
            {t("Contact us today for a free consultation and detailed project estimate.", "Contáctenos hoy para una consulta gratis y un presupuesto detallado de su proyecto.")}
          </p>
          <Link to="/contact">
            <Button className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-sm px-10 py-4 font-bold tracking-wide transition-all duration-300 uppercase text-sm" data-testid="contact-cta-btn">
              {t("Contact Us", "Contáctenos")} <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
